import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, Clock, Sparkles } from "lucide-react";

const LUNACAL_URL = "https://lunacal.ai/geniedawkins/discovery-call";

const Hero = () => (
  <section data-testid="book-hero" className="bg-[#FBF9F6] pt-16 md:pt-24 pb-10 md:pb-14">
    <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-16">
      <p className="text-orange-500 text-xs uppercase tracking-[0.28em] font-bold mb-6">Book your consult</p>
      <div className="grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-8">
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-black tracking-tight leading-[1.02]">
            Twenty quiet minutes. <em className="italic">One</em> honest plan.
          </h1>
          <p className="mt-7 max-w-2xl text-lg md:text-xl text-black/70 font-light leading-relaxed">
            Grab a chair, your coffee, and your kid's GPA if you have it handy. We'll talk about where they are, what they want, and exactly what 100% tuition coverage could look like for your family.
          </p>
        </div>
        <div className="md:col-span-4">
          <a
            href={LUNACAL_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="book-direct-link"
            className="inline-flex items-center justify-center gap-3 bg-black text-[#FBF9F6] w-full px-7 py-5 text-sm uppercase tracking-[0.18em] font-bold border-2 border-black hover:bg-orange-500 hover:border-orange-500 transition-colors"
          >
            Open in new tab
            <ArrowUpRight className="w-5 h-5" strokeWidth={2.5} />
          </a>
        </div>
      </div>
    </div>
  </section>
);

const Reassurance = () => {
  const items = [
    { icon: Clock, label: "20 minutes", sub: "Quick, focused, no fluff." },
    { icon: ShieldCheck, label: "No pressure", sub: "Free consult. No upsell traps." },
    { icon: Sparkles, label: "Real strategy", sub: "You'll leave with next steps." },
  ];
  return (
    <section data-testid="book-reassurance" className="bg-[#FBF9F6] pb-10 md:pb-14">
      <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-16">
        <div className="grid sm:grid-cols-3 gap-4 md:gap-6">
          {items.map((it, i) => (
            <div key={i} className="bg-white border-2 border-black/10 p-6 flex items-start gap-4" data-testid={`reassurance-${i}`}>
              <div className="w-11 h-11 bg-yellow-400 flex items-center justify-center flex-shrink-0">
                <it.icon className="w-5 h-5 text-black" strokeWidth={2.25} />
              </div>
              <div>
                <p className="font-serif text-xl font-bold leading-tight">{it.label}</p>
                <p className="text-sm text-black/60 mt-1 leading-relaxed">{it.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Embed = () => (
  <section data-testid="book-embed-section" className="bg-[#FBF9F6] pb-24 md:pb-32">
    <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-16">
      <div className="relative border-2 border-black bg-white">
        <div className="absolute -top-3 left-6 bg-black text-yellow-400 px-3 py-1.5 text-xs uppercase tracking-[0.2em] font-bold">
          Live calendar, lunacal.ai/geniedawkins/discovery-call
        </div>

        <div className="w-full" style={{ minHeight: "780px" }}>
          <iframe
            src={LUNACAL_URL}
            title="Book a consult with Genie Dawkins"
            data-testid="lunacal-iframe"
            className="w-full block"
            style={{ width: "100%", height: "820px", border: "0" }}
            loading="lazy"
            allow="camera; microphone; clipboard-write; fullscreen"
          />
        </div>

        <div className="border-t-2 border-black/10 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-[#FBF9F6]">
          <p className="text-sm text-black/65">
            Trouble loading the calendar?{" "}
            <a
              href={LUNACAL_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="book-fallback-link"
              className="text-orange-500 font-bold underline underline-offset-4 hover:text-black"
            >
              Book directly on Lunacal →
            </a>
          </p>
          <Link
            to="/services"
            data-testid="book-back-to-services"
            className="text-xs uppercase tracking-[0.2em] font-bold text-black hover:text-orange-500 transition-colors"
          >
            ← Re-read the services
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const What = () => (
  <section data-testid="book-what-to-expect" className="bg-black text-[#FBF9F6] py-20 md:py-28">
    <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-16">
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <p className="text-orange-400 text-xs uppercase tracking-[0.28em] font-bold mb-5">On the call</p>
          <h2 className="font-serif text-4xl md:text-5xl font-black tracking-tight leading-[1.05]">
            Here's what twenty <em className="italic text-yellow-400">unhurried</em> minutes looks like.
          </h2>
        </div>
        <div className="md:col-span-7">
          <ol className="space-y-7">
            {[
              { n: "01", t: "We meet your student where they are.", d: "Grade, grades, dreams, and what they secretly hope you don't tell anyone." },
              { n: "02", t: "We map what 100% covered could mean.", d: "Real schools, real scholarships, real numbers, no vague reassurance." },
              { n: "03", t: "You leave with a next step.", d: "Whether that's the $99 monthly list, the Pathway Program, or 'go away and breathe', we'll be honest about the fit." },
            ].map((s) => (
              <li key={s.n} className="grid grid-cols-[auto_1fr] gap-5 items-start">
                <span className="font-serif text-3xl font-black text-orange-400">{s.n}</span>
                <div>
                  <p className="font-serif text-xl md:text-2xl font-bold leading-snug">{s.t}</p>
                  <p className="mt-2 text-[#FBF9F6]/70 leading-relaxed">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>

          <Link
            to="/services"
            data-testid="book-secondary-cta"
            className="mt-10 inline-flex items-center gap-2 text-yellow-400 font-bold text-sm uppercase tracking-[0.18em] border-b-2 border-yellow-400 pb-1 hover:text-orange-400 hover:border-orange-400 transition-colors"
          >
            See the services first
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const BookNow = () => {
  return (
    <div data-testid="page-book">
      <Hero />
      <Reassurance />
      <Embed />
      <What />
    </div>
  );
};

export default BookNow;
